import React, {Component, ReactElement, useEffect, useRef, useState} from "react";
import {Button, Card, IconButton, Typography} from "@mui/material";
import {StyledBoxColumn, StyledBoxRow} from "../../../shared/StyledComponents";
import {DW_SM, PD_LG, PD_MD, PD_XLG, PD_XLG2, PD_XSM, SZ_SMD} from "../../../shared/dimens";
import {Country, MapDifficultyLevel, MapGameBoard, MapGameConfig, MapGameData} from "./types";
import {BaseApp, DIALOG_FLAG_SHOW_CLOSE} from "../../../shared/BaseApp";
import {DisplayMode, GameFragment, GameFragmentProps, GameFragmentState} from "../GameFragment";
import {FullscreenOutlined, HelpOutlineOutlined, ZoomInOutlined, ZoomOutOutlined} from "@mui/icons-material";

import {Game} from "../../types";
import {colorBlue} from "../../../shared/colors";
import {GameOverResult, GameOverResultType} from "../types";

const SHOW_FLAG = false;

export type MapGameFragmentProps = GameFragmentProps & {}

enum MapGameStatus {
  STATUS_IN_PROGRESS,
  STATUS_INCOMPLETE, // Finished, but NOT solved
  STATUS_COMPLETE, // Finished, and solved
}

enum CountrySelectStatus {
  NONE,
  CORRECT,
  INCORRECT,
}

type MapGameFragmentState = GameFragmentState<MapGameConfig, MapGameData> & {
  gameStatus: MapGameStatus,
  currentIndex: number,
  selectStatus: CountrySelectStatus,
  selectedCountry?: Country,
  tryCount: number,
  correctCount: number,
}

type Point = {
  x: number;
  y: number;
}

function MapView(props: {
  MapComponent: typeof React.Component<React.SVGProps<SVGSVGElement>>,
  countries: Country[],
  onCountrySelect: (country: Country) => void,
  revealCountry?: Country
}): ReactElement {
  const svgRef = useRef<SVGSVGElement>();
  const dragRef = useRef<boolean>();
  const [scaleLevel, setScaleLevel] = useState<number>(4);
  const [translate, setTranslate] = useState<Point>({x: 0, y: 0});
  const [dragStart, setDragStart] = useState<Point>();
  const [dragLast, setDragLast] = useState<Point>();
  useEffect(() => {
    for (const cc of props.countries) {
      const element: SVGPathElement = svgRef.current.getElementById(`cc_${cc.cc.toLowerCase()}`) as SVGPathElement;
      if (!element.onclick) {
        element.onclick = () => {
          if (dragRef.current) {
            return;
          }
          props.onCountrySelect(cc);
        };
      }
    }
  }, []);
  useEffect(() => {
    if (props.revealCountry) {
      const element: SVGPathElement = svgRef.current.getElementById(`cc_${props.revealCountry.cc.toLowerCase()}`) as SVGPathElement;
      element.classList.add("highlight");
      setTimeout(() => {
        element.classList.remove("highlight");
      }, 2000);
    }
  }, [props.revealCountry]);
  const scale = Math.pow(1.25, scaleLevel - 4);
  return <div
    onMouseDown={e => {
      setDragStart({x: e.clientX, y: e.clientY});
      setDragLast({x: e.clientX, y: e.clientY});
      dragRef.current = false;
    }}
    onMouseMove={e => {
      if (!dragStart) {
        return;
      }
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      if (Math.sqrt(dx * dx + dy * dy) >= 6) {
        dragRef.current = true;
      }
      setTranslate({
        x: translate.x + (e.clientX - dragLast.x) / scale,
        y: translate.y + (e.clientY - dragLast.y) / scale
      });
      setDragLast({x: e.clientX, y: e.clientY});
    }}
    onMouseUp={e => {
      setDragStart(undefined);
      setDragLast(undefined);
    }}
    style={{
      width: "100%",
      position: "relative",
      flexGrow: 1,
      display: "flex",
      overflow: "hidden",
      justifyContent: "center"
    }}>
    <props.MapComponent
      //@ts-ignore
      ref={svgRef}
      id={"map"}
      style={{
        width: "100%",
        transform: `scale(${scale}) translate(${translate.x}px, ${translate.y}px)`,
        aspectRatio: 2
      }}/>
    <Card style={{position: 'absolute', top: 24, left: 24, display: "flex", flexDirection: "column"}}>
      <Button
        style={{width: SZ_SMD, height: SZ_SMD}}
        onClick={e => {
          e.stopPropagation();
          if (scaleLevel >= 10) {
            return;
          }
          setScaleLevel(scaleLevel + 1);
        }}>
        <ZoomInOutlined/>
      </Button>
      <Button
        style={{width: SZ_SMD, height: SZ_SMD}}
        onClick={e => {
          e.stopPropagation();
          if (scaleLevel <= 1) {
            return;
          }
          setScaleLevel(scaleLevel - 1);
        }}>
        <ZoomOutOutlined/>
      </Button>
    </Card>
  </div>;
}

export abstract class MapGameFragment<LEVEL extends MapDifficultyLevel> extends GameFragment<MapGameFragmentProps, MapGameConfig, MapGameData, MapGameFragmentState> {

  private board: MapGameBoard;

  protected abstract getGame(): Game;

  protected onCreateState(): MapGameFragmentState {
    return {
      ...super.onCreateState(),
      gameStatus: MapGameStatus.STATUS_IN_PROGRESS,
      currentIndex: 0,
      tryCount: 0,
      correctCount: 0,
      selectStatus: CountrySelectStatus.NONE,
      displayMode: DisplayMode.IMMERSIVE,
    };
  }

  componentDidUpdate(prevProps: Readonly<MapGameFragmentProps>, prevState: Readonly<MapGameFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.gameData !== this.state.gameData) {
      this.board = this.state.gameData.gamePlay.jsonData as MapGameBoard;
    }
    if (prevState.currentIndex !== this.state.currentIndex) {
      const board = this.board;
      const complete: boolean = this.state.currentIndex >= board.layout.countries.length;
      if (complete) {
        this.props.gameFragmentListener?.onStatusChanged("Game complete.");
        let resultText = this.getResultText();
        this.finishGame(new GameOverResult(this.getGame().id, GameOverResultType.WON, this.board.level.name, this.board.getPoints(this.state.correctCount), 0, resultText, false));
        this.setState({
          gameStatus: MapGameStatus.STATUS_COMPLETE,
        });
      }
    }
  }

  protected abstract getLevels(): LEVEL[];

  protected abstract getResultText(): string;

  protected async fetchOnMount(forceReload?: boolean): Promise<void> {
    const gameConfig = this.state.gameConfig;
    if (!gameConfig) {
      return;
    }
    this.setState({
      gameData: await MapGameData.create(gameConfig),
    });
  }

  protected renderConfigContent(): React.ReactElement {
    return this.renderConfigContentForGame(this.getGame(), <>
      {this.getLevels().map(level =>
        <Button variant="contained"
                onClick={() => this.setState({gameConfig: new MapGameConfig(level)})}>{level.text}</Button>)}
    </>);
  }

  protected renderToolbarTitle(): React.ReactElement {
    return <>
      <Typography
        variant="h6"
        style={{marginLeft: PD_MD,}}>
        {(this.state.gameData.gamePlay.jsonData as MapGameBoard).level.text}
      </Typography>
      <StyledBoxRow style={{
        position: "absolute",
        left: 0,
        top: 0,
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%"
      }}>
        <Typography variant="h6">
          Score: {this.state.correctCount},
          &nbsp;Q{Math.min(this.board.layout.countries.length, this.state.currentIndex + 1)} of {this.board.layout.countries.length}
        </Typography>
      </StyledBoxRow>
    </>;
  }

  protected renderToolbarButtons() {
    return <>
      <IconButton onClick={() => BaseApp.CONTEXT.showDialog({flags: DIALOG_FLAG_SHOW_CLOSE}, props => {
        return <StyledBoxColumn style={{padding: PD_LG, maxWidth: DW_SM, alignItems: "center", gap: PD_LG}}>
          <Typography variant="h5">How to play Map Quiz</Typography>
          <Typography>Find the country on the map and click. If you're right, you score a point. You get a total of
            three chances to get it right.</Typography>
        </StyledBoxColumn>;
      })}>
        <HelpOutlineOutlined/>
      </IconButton>
      <IconButton onClick={() => {
        this.setState({displayMode: DisplayMode.IMMERSIVE});
      }}>
        <FullscreenOutlined/>
      </IconButton>
    </>
  }

  protected abstract allCountries(): Country[];

  protected abstract getMapComponent(): typeof Component;

  protected renderGameContent(): ReactElement | null {
    const board = this.board;
    if (!board) {
      return null;
    }
    const currentCountry = this.board.layout.countries[this.state.currentIndex];
    const complete = this.state.currentIndex >= board.layout.countries.length;
    return <div style={{
      position: "relative",
      width: "100%",
      display: "flex",
      flexBasis: 0,
      flexGrow: 1,
      background: colorBlue
    }}>
      <MapView
        countries={this.allCountries()}
        MapComponent={this.getMapComponent()}
        onCountrySelect={country => this.onCheckCountrySelect(country)}
        revealCountry={this.state.tryCount >= 3 ? currentCountry : null}/>
      <div style={{
        pointerEvents: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "end",
        position: "absolute",
        width: "100%",
        height: "25vh",
        bottom: 0,
        background: "linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, .2))"
      }}>
        {!complete
          ? <div style={{padding: `${PD_XLG}px ${PD_XLG2}px`, background: "white"}}>
            {this.state.selectStatus === CountrySelectStatus.NONE
              ? <Typography variant="h5">Where is {this.renderCountryName(currentCountry)}?</Typography>
              : null}
            {this.state.selectStatus === CountrySelectStatus.CORRECT
              ? <Typography variant="h5">That is Correct!</Typography>
              : null}
            {this.state.selectStatus === CountrySelectStatus.INCORRECT
              ? <Typography variant="h5">No, that is {this.renderCountryName(this.state.selectedCountry)}</Typography>
              : null}
          </div>
          : null}
      </div>
    </div>;
  }

  private renderCountryName(country: Country): ReactElement {
    if (!country) {
      return null;
    }
    return <>
      {country.name}
      {SHOW_FLAG
        ? <img src={`https://flagcdn.com/w20/${country.cc.toLowerCase()}.png`}
               style={{display: "inline", width: 24, height: 24, objectFit: "cover", marginRight: PD_XSM}}/>
        : null}
    </>
  }

  private selectTimeout: any;

  private onCheckCountrySelect(country: Country): void {
    if (this.state.tryCount >= 3 || (this.state.currentIndex >= this.board.layout.countries.length)) {
      return;
    }
    clearTimeout(this.selectTimeout);
    const answer = this.board.layout.countries[this.state.currentIndex];
    const tryCount = this.state.tryCount + 1;
    const reveal = tryCount >= 3;
    let correct: boolean = answer.cc === country.cc;
    if (correct) {
      this.setState({
        selectStatus: CountrySelectStatus.CORRECT,
        selectedCountry: country,
        tryCount: 0,
        correctCount: this.state.correctCount + 1,
      });
    } else {
      this.setState({
        selectStatus: CountrySelectStatus.INCORRECT,
        selectedCountry: country,
        tryCount: tryCount,
      });
    }
    this.selectTimeout = setTimeout(() => {
      this.setState({
        currentIndex: this.state.currentIndex + ((correct || reveal) ? 1 : 0),
        selectStatus: CountrySelectStatus.NONE,
        selectedCountry: undefined,
        tryCount: (correct || reveal) ? 0 : tryCount,
      });
    }, 2000);
  }
}